import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut,
  MessageSquare,
  BarChart3,
  Settings as SettingsIcon,
  Search,
  Loader2,
  Bell,
  BellOff,
  HelpCircle,
  Users,
  TicketIcon,
  BookOpen,
  X,
  SlidersHorizontal,
  Sparkles,
  LayoutPanelLeft,
} from 'lucide-react';
import { api, type SearchHit } from '../lib/api';
import { useUiMode, type UiMode } from '../lib/uiMode';
import { TenantSwitcher } from '../tenant/TenantSwitcher';
import { useResource } from '../hooks/useResource';
import type { Inbox } from '../lib/types';
import {
  isInboxOpen,
  hasBusinessHours,
  type BusinessHours,
} from '../lib/businessHours';

const MODE_LABEL: Record<UiMode, string> = {
  novice: 'Simple',
  advanced: 'Advanced',
  new: 'New',
};

const MODE_TITLE: Record<UiMode, string> = {
  novice: 'Simple view — secondary controls are hidden. Click for the advanced view.',
  advanced: 'Advanced view — every control is shown. Click for the new sidebar layout.',
  new: 'New layout — queues and filters live in the left sidebar. Click for the simple view.',
};

interface TopBarProps {
  tenantCode: string;
  userEmail?: string;
  onSignOut: () => void;
  searchInputRef?: React.MutableRefObject<HTMLInputElement | null>;
  notificationsEnabled?: boolean;
  onToggleNotifications?: () => void;
}

export function TopBar({
  tenantCode,
  userEmail,
  onSignOut,
  searchInputRef,
  notificationsEnabled,
  onToggleNotifications,
}: TopBarProps) {
  const navigate = useNavigate();
  const { mode, advanced, toggle: toggleUiMode } = useUiMode();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (!tenantCode || q.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setBusy(true);
      try {
        const r = await api.searchChats(tenantCode, q.trim());
        setResults(r.items);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q, tenantCode]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function pick(hit: SearchHit) {
    setOpen(false);
    setQ('');
    if (hit.chatId) {
      navigate(`/inbox/${hit.chatId}`);
    } else if (hit.contactId) {
      navigate('/inbox');
    }
  }

  function setRef(el: HTMLInputElement | null) {
    localInputRef.current = el;
    if (searchInputRef) searchInputRef.current = el;
  }

  return (
    <div className="fixed top-0 left-0 right-0 h-10 bg-primary-600 text-white flex items-center justify-between px-2 sm:px-4 z-50 gap-2">
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className="font-medium flex items-center gap-1.5">
          <MessageSquare className="w-4 h-4 opacity-80" />
          <span className="hidden sm:inline">ChatOlam</span>
        </span>
      </div>

      <div ref={wrapRef} className="relative flex-1 min-w-0 max-w-md sm:mx-4">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-primary-200" />
          <input
            ref={setRef}
            type="text"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="Search email, phone, message…"
            className="w-full pl-8 pr-2 py-1 text-xs rounded bg-primary-700/60 placeholder-primary-200 focus:outline-none focus:bg-primary-700 focus:ring-1 focus:ring-primary-300 text-white"
          />
          {busy && (
            <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-primary-200 animate-spin" />
          )}
        </div>
        {open && q.trim().length >= 2 && (
          <div className="absolute top-full mt-1 left-0 right-0 bg-white text-gray-900 rounded-md shadow-lg max-h-80 overflow-y-auto border border-gray-200">
            {results.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-500">
                {busy ? 'Searching…' : 'No matches.'}
              </div>
            ) : (
              results.map((hit, i) => (
                <button
                  key={`${hit.type}:${hit.chatId ?? hit.contactId}:${i}`}
                  type="button"
                  onClick={() => pick(hit)}
                  className="block w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase font-semibold text-gray-500">
                      {hit.type}
                    </span>
                    <span className="text-xs font-medium text-gray-900 truncate">
                      {hit.name || hit.email || hit.phone || hit.contactId}
                    </span>
                  </div>
                  {hit.preview && (
                    <div className="text-[11px] text-gray-600 line-clamp-2 mt-0.5" dir="auto">
                      {hit.preview}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <nav className="flex items-center gap-1 text-sm flex-shrink-0">
        {/* Simple → Advanced → New, cycling. Simple/Advanced only hide or show
            secondary controls; New additionally swaps the inbox chrome for the
            left sidebar. No functionality changes in any of the three — see
            lib/uiMode.ts. */}
        <button
          type="button"
          onClick={toggleUiMode}
          data-testid="ui-mode-toggle"
          aria-pressed={mode !== 'novice'}
          className={`px-2 py-1 rounded transition-colors flex items-center gap-1 ${
            mode === 'novice' ? 'hover:bg-primary-700' : 'bg-primary-800 hover:bg-primary-700'
          }`}
          title={MODE_TITLE[mode]}
        >
          {mode === 'advanced' ? (
            <SlidersHorizontal className="w-4 h-4" />
          ) : mode === 'new' ? (
            <LayoutPanelLeft className="w-4 h-4" />
          ) : (
            <Sparkles className="w-4 h-4 opacity-80" />
          )}
          <span className="hidden xl:inline text-[10px] uppercase tracking-wide">
            {MODE_LABEL[mode]}
          </span>
        </button>
        {onToggleNotifications && (
          <button
            type="button"
            onClick={onToggleNotifications}
            className="px-2 py-1 rounded hover:bg-primary-700 transition-colors"
            title={notificationsEnabled ? 'Disable browser notifications' : 'Enable browser notifications'}
          >
            {notificationsEnabled ? (
              <Bell className="w-4 h-4" />
            ) : (
              <BellOff className="w-4 h-4 opacity-70" />
            )}
          </button>
        )}
        <button
          type="button"
          onClick={() => setShowHelp(true)}
          className="px-2 py-1 rounded hover:bg-primary-700 transition-colors"
          title="Keyboard shortcuts"
        >
          <HelpCircle className="w-4 h-4" />
        </button>
        <Link
          to="/inbox"
          title="Inbox"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <MessageSquare className="w-4 h-4" />
          <span className="hidden md:inline">Inbox</span>
        </Link>
        <Link
          to="/contacts"
          title="Contacts"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <Users className="w-4 h-4" />
          <span className="hidden md:inline">Contacts</span>
        </Link>
        <Link
          to="/tickets"
          title="Tickets — the team's issue list and customer tickets"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <TicketIcon className="w-4 h-4" />
          <span className="hidden md:inline">Tickets</span>
        </Link>
        <Link
          to="/guides"
          title="Guides — support documents and how-to videos"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <BookOpen className="w-4 h-4" />
          <span className="hidden md:inline">Guides</span>
        </Link>
        <Link
          to="/reports"
          title="Reports"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <BarChart3 className="w-4 h-4" />
          <span className="hidden md:inline">Reports</span>
        </Link>
        <Link
          to="/settings"
          title="Settings"
          className="px-2 sm:px-3 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1.5"
        >
          <SettingsIcon className="w-4 h-4" />
          <span className="hidden md:inline">Settings</span>
        </Link>
        <div className="hidden md:block w-px h-5 bg-primary-500 mx-2" />
        <WorkingHours />
        <TenantSwitcher />
        {advanced && (
          <span
            className="hidden md:inline text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded text-primary-200 bg-primary-700/70"
            title="Tenant"
          >
            {tenantCode}
          </span>
        )}
        <span className="hidden lg:inline text-xs text-primary-100">{userEmail}</span>
        <button
          onClick={onSignOut}
          className="px-2 py-1 rounded hover:bg-primary-700 transition-colors flex items-center gap-1 text-xs text-primary-100"
          title="Sign out"
        >
          <LogOut className="w-3.5 h-3.5" />
        </button>
      </nav>

      {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
}

// Inbox shape with the optional businessHours field (not on the shared agent-ui
// Inbox type). The backend writes this; we read it defensively.
type InboxWithHours = Inbox & { businessHours?: BusinessHours };

const EST_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

// Live EST clock + Open/Closed pill driven by the first inbox that has
// businessHours configured. Falls back to "Open 24/7" when none is configured.
function WorkingHours() {
  const { items } = useResource<InboxWithHours>('inboxes');
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const scheduledInbox = items.find((i) => hasBusinessHours(i.businessHours));
  const clock = EST_FMT.format(now);

  let pill: React.ReactNode;
  if (scheduledInbox) {
    const openNow = isInboxOpen(scheduledInbox.businessHours, now);
    pill = (
      <span
        className={
          'text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded flex items-center gap-1 ' +
          (openNow ? 'bg-green-500/90 text-white' : 'bg-gray-500/80 text-white')
        }
        title={
          openNow
            ? `${scheduledInbox.name} is within business hours`
            : `${scheduledInbox.name} is outside business hours`
        }
      >
        <span
          className={
            'w-1.5 h-1.5 rounded-full ' + (openNow ? 'bg-white' : 'bg-white/70')
          }
        />
        {openNow ? 'Open' : 'Closed'}
      </span>
    );
  } else {
    pill = (
      <span
        className="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-primary-700/70 text-primary-100"
        title="No business hours configured — always available"
      >
        Open 24/7
      </span>
    );
  }

  return (
    <span className="hidden lg:flex items-center gap-1.5 text-xs text-primary-100">
      {pill}
      <span className="tabular-nums font-medium text-white" title="Current time (US/Eastern)">
        {clock}
      </span>
      <span className="text-[10px] text-primary-200">EST</span>
    </span>
  );
}

const SHORTCUTS: Array<{ key: string; desc: string }> = [
  { key: 'j', desc: 'Next conversation' },
  { key: 'k', desc: 'Previous conversation' },
  { key: 'r', desc: 'Resolve current conversation' },
  { key: 's', desc: 'Snooze 1 hour' },
  { key: 'a', desc: 'Assign to me' },
  { key: '/', desc: 'Focus search' },
  { key: 'Esc', desc: 'Clear selection / close menus' },
];

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 bg-black/40 z-[100] flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-white text-gray-900 rounded-md shadow-xl w-80 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-semibold">Keyboard shortcuts</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-4 h-4" />
          </button>
        </div>
        <table className="w-full text-xs">
          <tbody>
            {SHORTCUTS.map((s) => (
              <tr key={s.key} className="border-b border-gray-100 last:border-0">
                <td className="py-1.5 pr-3">
                  <kbd className="px-1.5 py-0.5 bg-gray-100 border border-gray-300 rounded font-mono text-[11px]">
                    {s.key}
                  </kbd>
                </td>
                <td className="py-1.5 text-gray-700">{s.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-3 text-[11px] text-gray-500">
          Shortcuts are disabled while typing in an input.
        </p>
      </div>
    </div>
  );
}
